import { useCallback, useMemo, useState } from 'react'
import {
  Background,
  type EdgeTypes,
  type Node,
  type NodeTypes,
  MarkerType,
  ReactFlow,
  ReactFlowProvider
} from '@xyflow/react'

import type {
  DisplayGroupNode as DisplayGroupNodeType,
  StateMachineEdge,
  StateMachineNode,
  StateMachineState
} from '../../ProgramStateMachine.types'
import { useStateMachineEditorContext } from '../../context/StateMachineEditorContext'
import { AddStateNode } from '../AddStateNode/AddStateNode'
import { CanvasToolbar } from '../CanvasToolbar/CanvasToolbar'
import { DisplayGroupNode } from '../DisplayGroupNode/DisplayGroupNode'
import { StateMachineEdge as StateMachineEdgeComponent } from '../StateMachineEdge/StateMachineEdge'
import { StateMachineNode as StateMachineNodeComponent } from '../StateMachineNode/StateMachineNode'
import { StateMachineSidePanel } from '../StateMachineSidePanel/StateMachineSidePanel'

import { Flex } from '@percent/lemonade'

const ADD_STATE_NODE_ID = '__add_state__'
const NODE_VERTICAL_SPACING = 160

interface StateMachineBlockVisualiserProps {
  nodes: StateMachineNode[]
  edges: StateMachineEdge[]
  groupNodes: DisplayGroupNodeType[]
  onNodeClick: (event: React.MouseEvent, node: StateMachineNode) => void
  onPaneClick: () => void
  onAddState: () => void
  selectedElement: StateMachineNode | StateMachineEdge | null
}

const defaultEdgeOptions = {
  markerEnd: { type: MarkerType.ArrowClosed }
}

export function StateMachineBlockVisualiser({
  nodes,
  edges,
  groupNodes,
  onNodeClick,
  onPaneClick,
  onAddState,
  selectedElement
}: Readonly<StateMachineBlockVisualiserProps>) {
  const { localDefinition } = useStateMachineEditorContext()
  const [selectedGroupName, setSelectedGroupName] = useState<string | null>(null)
  const [isAddingGroup, setIsAddingGroup] = useState(false)

  const handleAddState = useCallback(() => {
    setSelectedGroupName(null)
    setIsAddingGroup(false)
    onAddState()
  }, [onAddState])

  const handleAddGroup = useCallback(() => {
    setSelectedGroupName(null)
    setIsAddingGroup(true)
  }, [])

  const nodeTypes: NodeTypes = useMemo(
    () => ({
      stateMachineNode: StateMachineNodeComponent,
      addStateNode: AddStateNode,
      displayGroupNode: DisplayGroupNode
    }),
    []
  )

  const edgeTypes: EdgeTypes = useMemo(
    () => ({
      stateMachineEdge: StateMachineEdgeComponent
    }),
    []
  )

  const terminalNodes = useMemo(() => nodes.filter(n => n.data.isTerminal), [nodes])

  // Build a set of member state IDs grouped under each group node for interleaving
  const groupMemberMap = useMemo(() => {
    const map = new Map<string, Set<string>>()

    for (const node of nodes) {
      if (node.parentId) {
        if (!map.has(node.parentId)) map.set(node.parentId, new Set())
        map.get(node.parentId)!.add(node.id)
      }
    }

    return map
  }, [nodes])

  const allNodes = useMemo<Node[]>(() => {
    // Compute maxY from ungrouped state nodes and group node bottom edges
    const ungroupedMaxY = nodes.length > 0 ? Math.max(0, ...nodes.filter(n => !n.parentId).map(n => n.position.y)) : 0
    const groupBottomMaxY =
      groupNodes.length > 0
        ? Math.max(0, ...groupNodes.map(n => n.position.y + (typeof n.style?.height === 'number' ? n.style.height : 0)))
        : 0
    const maxY = Math.max(ungroupedMaxY, groupBottomMaxY)

    // Center the combined add-state node below the terminal nodes' absolute center x positions
    const terminalCenterXs = terminalNodes.map(n => {
      const nodeWidth = n.data.estimatedWidth ?? 160
      const absTopLeftX = n.parentId
        ? (groupNodes.find(g => g.id === n.parentId)?.position.x ?? 0) + n.position.x
        : n.position.x

      return absTopLeftX + nodeWidth / 2
    })
    const avgTerminalCenterX =
      terminalCenterXs.length > 0 ? terminalCenterXs.reduce((sum, x) => sum + x, 0) / terminalCenterXs.length : 0
    // AddStateNode has 2 buttons at min-width 140px each + borders ≈ 284px total
    const addStateNodeWidth = 284
    const avgTerminalX = avgTerminalCenterX - addStateNodeWidth / 2

    // Interleave: group nodes must appear before their children (xyflow requirement)
    const groupedStateIds = new Set<string>()

    for (const members of groupMemberMap.values()) {
      for (const id of members) {
        groupedStateIds.add(id)
      }
    }

    const interleaved: Node[] = []

    // Add ungrouped state nodes first
    for (const node of nodes) {
      if (!groupedStateIds.has(node.id)) {
        interleaved.push({
          ...node,
          selected: selectedElement?.id === node.id
        })
      }
    }

    // Then group nodes with their children
    for (const gNode of groupNodes) {
      interleaved.push({
        ...gNode,
        selected: selectedGroupName === gNode.data.groupName
      })

      const memberIds = groupMemberMap.get(gNode.id)

      if (memberIds) {
        for (const node of nodes) {
          if (memberIds.has(node.id)) {
            interleaved.push({
              ...node,
              selected: selectedElement?.id === node.id
            })
          }
        }
      }
    }

    // Add combined add-state / add-group node
    interleaved.push({
      id: ADD_STATE_NODE_ID,
      type: 'addStateNode',
      position: { x: avgTerminalX, y: maxY + NODE_VERTICAL_SPACING },
      data: { onAddState: handleAddState, onAddGroup: handleAddGroup },
      selectable: false,
      draggable: false
    })

    return interleaved
  }, [
    nodes,
    groupNodes,
    selectedElement,
    selectedGroupName,
    terminalNodes,
    groupMemberMap,
    handleAddState,
    handleAddGroup
  ])

  const allEdges = useMemo<StateMachineEdge[]>(() => {
    const addStateEdges: StateMachineEdge[] = terminalNodes.map(node => ({
      id: `${node.id}-to-add-state`,
      source: node.id,
      target: ADD_STATE_NODE_ID,
      sourceHandle: `${node.id}-source-0`,
      targetHandle: `${ADD_STATE_NODE_ID}-target-0`,
      type: 'stateMachineEdge',
      style: { strokeDasharray: '5,5', stroke: '#cbd5e1' },
      data: {}
    }))

    return [...edges, ...addStateEdges]
  }, [edges, terminalNodes])

  const handleNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      if (node.id === ADD_STATE_NODE_ID) {
        // Click handling is done via buttons inside the node
        return
      }

      if (node.type === 'displayGroupNode') {
        setIsAddingGroup(false)
        setSelectedGroupName(String(node.data.groupName ?? ''))
        onPaneClick() // clear parent's selectedElement so previous state node deselects
      } else {
        setSelectedGroupName(null)
        setIsAddingGroup(false)
        // Look up the full typed node from our nodes array
        const typedNode = nodes.find(n => n.id === node.id)

        if (typedNode) {
          onNodeClick(event, typedNode)
        }
      }
    },
    [onNodeClick, nodes]
  )

  const handlePaneClick = useCallback(() => {
    setSelectedGroupName(null)
    setIsAddingGroup(false)
    onPaneClick()
  }, [onPaneClick])

  const selectedState: StateMachineState | null = useMemo(() => {
    if (!selectedElement || 'source' in selectedElement) return null

    if (selectedGroupName || isAddingGroup) return null

    return localDefinition?.states.find(s => s.id === selectedElement.data?.stateId) ?? null
  }, [selectedElement, localDefinition, selectedGroupName, isAddingGroup])

  const isAddingState = selectedElement?.id === ADD_STATE_NODE_ID && !isAddingGroup && !selectedGroupName

  const showPanel = selectedState !== null || isAddingState || selectedGroupName !== null || isAddingGroup

  return (
    <ReactFlowProvider>
      <CanvasToolbar />
      <Flex direction="row" style={{ height: '800px', width: '100%' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <ReactFlow
            nodes={allNodes}
            edges={allEdges}
            nodesDraggable={false}
            nodesConnectable={false}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={defaultEdgeOptions}
            onNodeClick={handleNodeClick}
            onPaneClick={handlePaneClick}
            fitView
          >
            <Background />
          </ReactFlow>
        </div>

        {showPanel && (
          <StateMachineSidePanel
            selectedState={selectedState}
            isAddingState={isAddingState}
            selectedGroup={selectedGroupName}
            isAddingGroup={isAddingGroup}
            onClose={handlePaneClick}
          />
        )}
      </Flex>
    </ReactFlowProvider>
  )
}
